<template>
  <div>
    <Disclosure as="div" v-slot="{ open }">
      <div
        :class="`flex flex-col gap-2 py-3 px-4 w-full md:grid md:grid-cols-6 lg:grid-cols-12 md:gap-1 md:items-center md:py-2 my-0 hover:bg-gray-50 ${
          open ? '' : 'border-b border-gray-200'
        }`"
      >
        <!-- Mobile: card layout -->
        <div class="flex gap-3 md:hidden">
          <v-avatar color="primary" class="shrink-0 mt-0.5">
            <ProtectedAvatar :src="resolvedAvatar" :size="40">
              <span class="text-md font-bold">{{
                getInitalsName(props.data.user.name || 'ME')
              }}</span>
            </ProtectedAvatar>
          </v-avatar>
          <div class="flex flex-col gap-1.5 min-w-0 flex-1">
            <div>
              <p class="text-left text-sm font-medium">{{ props.data.user?.name }}</p>
              <p class="text-left text-xs text-gray-400 truncate">{{ props.data.user?.email }}</p>
            </div>
            <div v-if="props.data.user.mainPhone?.phoneNumber" class="text-left">
              <span class="text-xs text-gray-400">Telefone: </span>
              <span class="text-xs text-gray-600">{{
                formatPhone(props.data.user.mainPhone.phoneNumber)
              }}</span>
            </div>
            <div v-if="props.data.companies?.length" class="text-left">
              <span class="text-xs text-gray-400 block mb-0.5">Empresas</span>
              <TableChips :items="props.data.companies" />
            </div>
            <div class="flex items-center gap-1 pt-1">
              <v-tooltip :text="props.data.user.isActive ? 'Desativar usuário' : 'Ativar usuário'">
                <template #activator="{ props: tooltipProps }">
                  <span v-bind="tooltipProps" class="inline-flex">
                    <DsSwitch
                      :model-value="props.data.user.isActive"
                      :loading="loadingToggle"
                      :disabled="!canUserCreating"
                      @update:model-value="(val: boolean) => toggleUserStatus(props.data.user, val)"
                    />
                  </span>
                </template>
              </v-tooltip>
              <v-btn
                variant="text"
                density="compact"
                color="primary"
                :icon="mdiPencil"
                @click="emitEditUser"
                class="flex-shrink-0"
                :disabled="!canUserCreating"
              />
              <DisclosureButton as="template" :disabled="!canUserCreating">
                <v-btn
                  variant="text"
                  density="compact"
                  color="primary"
                  :icon="mdiLockReset"
                  class="flex-shrink-0"
                />
              </DisclosureButton>
              <v-btn
                data-testid="permissions-user-button-mobile"
                variant="text"
                density="compact"
                color="primary"
                :icon="mdiShieldAccount"
                @click="handleOpenPermissions"
                class="flex-shrink-0"
                :disabled="!canUserCreating"
              />
            </div>
          </div>
        </div>

        <!-- Desktop: grid layout -->
        <div class="hidden md:flex md:col-span-4 lg:col-span-3 items-center gap-3">
          <v-avatar color="primary" class="shrink-0">
            <ProtectedAvatar :src="resolvedAvatar" :size="40">
              <span class="text-md font-bold">{{
                getInitalsName(props.data.user.name || 'ME')
              }}</span>
            </ProtectedAvatar>
          </v-avatar>
          <p class="text-left">{{ props.data.user?.name }}</p>
        </div>
        <div class="lg:block hidden lg:col-span-3">
          <p class="text-left truncate ...">{{ props.data.user?.email }}</p>
        </div>
        <div class="text-left lg:block hidden lg:col-span-2">
          <p>{{ formatPhone(props.data.user.mainPhone?.phoneNumber) }}</p>
        </div>
        <div class="lg:block hidden lg:col-span-2">
          <TableChips :items="props.data.companies" />
        </div>
        <div class="hidden md:block md:col-span-2 lg:col-span-2">
          <div class="flex items-center justify-around flex-nowrap">
            <v-tooltip :text="props.data.user.isActive ? 'Desativar usuário' : 'Ativar usuário'">
              <template #activator="{ props: tooltipProps }">
                <span v-bind="tooltipProps" class="inline-flex">
                  <DsSwitch
                    :model-value="props.data.user.isActive"
                    :loading="loadingToggle"
                    :disabled="!canUserCreating"
                    @update:model-value="(val: boolean) => toggleUserStatus(props.data.user, val)"
                  />
                </span>
              </template>
            </v-tooltip>

            <v-tooltip text="Editar Dados do Usuário">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  variant="text"
                  density="compact"
                  color="primary"
                  :icon="mdiPencil"
                  @click="emitEditUser"
                  class="ml-1 flex-shrink-0"
                  :disabled="!canUserCreating"
                />
              </template>
            </v-tooltip>

            <v-tooltip text="Redefinir Senha">
              <template #activator="{ props: tooltipProps }">
                <DisclosureButton as="template" :disabled="!canUserCreating">
                  <v-btn
                    v-bind="tooltipProps"
                    variant="text"
                    density="compact"
                    color="primary"
                    :icon="mdiLockReset"
                    class="ml-1 flex-shrink-0"
                  />
                </DisclosureButton>
              </template>
            </v-tooltip>

            <v-tooltip text="Visualizar permissões">
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  data-testid="permissions-user-button"
                  variant="text"
                  density="compact"
                  color="primary"
                  :icon="mdiShieldAccount"
                  @click="handleOpenPermissions"
                  class="ml-1 flex-shrink-0"
                  :disabled="!canUserCreating"
                />
              </template>
            </v-tooltip>
          </div>
        </div>
      </div>

      <DisclosurePanel :class="`w-full py-4 ${open ? 'border-b border-gray-200' : ''}`">
        <div class="grid grid-cols-1 xl:grid-cols-4 lg:grid-cols-3 gap-4 pb-6 my-0">
          <Input
            @change="() => validatePassword()"
            :isError="isErrorPassword"
            v-model="form.newPassword"
            errorMessage="A senha precisa ter pelo menos 8 caracters, 1 letra maíuscula, 1 caracter especial e 1 número"
            type="password"
            pattern="^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[$*&@#])[0-9a-zA-Z$*&@#]{8,}$"
            width="100%"
            label="Senha"
            required
          />
          <Input
            @change="() => validateConfirmPassword()"
            :isError="isErrorConfirmPassword"
            v-model="form.confirmPassword"
            errorMessage="As senhas não coincidem!"
            type="password"
            pattern="^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[$*&@#])[0-9a-zA-Z$*@#]{8,}$"
            width="100%"
            label="Senha"
            required
          />
          <OutlineButton
            @click="handleSendPassword"
            width="100%"
            style="max-height: 52px"
            class="mr-4"
            text="Confirmar alteração de senha"
            :loading="loading"
          />
        </div>
      </DisclosurePanel>
    </Disclosure>

    <Modal
      :modalStateProp="modalState"
      @close="() => (modalState = false)"
      :user="props.data.user"
    />
  </div>
</template>

<script setup lang="ts">
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import { computed, reactive, ref } from 'vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import Modal from '../../../modules/company/Users/components/DeleteUserModal/Index.vue'
import Input from '../Inputs/Input.vue'
import Network from '@/Network'
import { CompanyUserResponse } from '@/Network/Types/Responses/CompanyResponses'
import { useToast } from 'vue-toastification'
import TableChips from '@/components/designSystem/Chips/TableChips.vue'
import { mdiLockReset, mdiPencil, mdiShieldAccount } from '@mdi/js'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import { resolveAvatarSrc } from '@/utils/avatar'
import { formatPhone } from '@/utils/formatters'

const props = defineProps<{
  data: CompanyUserResponse
}>()

const resolvedAvatar = computed(() => resolveAvatarSrc(props.data.user.avatarUrl))

const modalState = ref(false)
const loadingToggle = ref(false)
const isErrorPassword = ref(false)
const isErrorConfirmPassword = ref(false)
const isValid = ref(false)
const loading = ref(false)
const toast = useToast()
const emit = defineEmits(['edit-user'])

const permissionsStore = usePermissionsStore()
const modalStore = useModalStore()

const emitEditUser = () => {
  const payload = {
    user: props.data.user,
    companies: props.data.companies
  }
  emit('edit-user', payload)
}

const handleOpenPermissions = () => {
  modalStore.toggleModal(EModals.PERMISSIONS_TREE, {
    subjectId: props.data.user.id,
    subjectType: AuthorizationResourcesAndSubjects.User,
    subjectName: props.data.user?.name
  })
}

const form = reactive({
  radio: 'Dashboard',
  checkbox: false,
  newPassword: '',
  confirmPassword: ''
})

function validatePassword() {
  const regex = new RegExp(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[$*&@#])[0-9a-zA-Z$*&@#]{8,}$/)
  if (form.newPassword === '' || !regex.test(form.newPassword)) {
    isValid.value = false
    isErrorPassword.value = true
  } else {
    isValid.value = true
    isErrorPassword.value = false
  }
}

function validateConfirmPassword() {
  const isEqualPasswords = form.confirmPassword === form.newPassword

  if (form.confirmPassword === '') {
    isValid.value = false
    isErrorConfirmPassword.value = true
  } else if (!isEqualPasswords) {
    isValid.value = false
    isErrorConfirmPassword.value = true
  } else {
    isValid.value = true
    isErrorConfirmPassword.value = false
  }
}

const handleSendPassword = async () => {
  if (form.newPassword.length === 0 || form.confirmPassword.length === 0) return
  try {
    loading.value = true
    await Network.company.changeUserCompanyPassword(props.data.user.id, {
      password: form.newPassword,
      confirmPassword: form.confirmPassword
    })
    toast.success(`Usuário ${props.data.user.name} teve a senha atualizada com sucesso!`)

    form.newPassword = ''
    form.confirmPassword = ''
  } catch (error: any) {
    const errorMessage = error.response?.data?.message
    toast.error(
      errorMessage || 'Ocorreu algum erro, verifique os dados inseridos e tente novamente!'
    )
    console.error(error)
  } finally {
    loading.value = false
  }
}

const getInitalsName = (name: string) => {
  return name.substring(0, 2).toUpperCase()
}

const toggleUserStatus = async (user: { id: number; isActive: boolean }, newStatus: boolean) => {
  try {
    loadingToggle.value = true
    await Network.user.toggleUserStatus(user.id, newStatus)
    user.isActive = newStatus
    toast.success(`Usuário ${newStatus ? 'ativado' : 'desativado'} com sucesso!`)
  } catch (error) {
    toast.error('Erro ao alternar status do usuário. Tente novamente.')
    console.error(error)
  } finally {
    loadingToggle.value = false
  }
}

const canUserCreating = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.CompanyUserCreating,
    resourceType: AuthorizationResourcesAndSubjects.Company
  })
})
</script>
